import { ArrowLeft } from 'lucide-react';
import { useExternalOis, useOiDetailLive } from '@/lib/api/queries';
import type { DssOi } from '@/lib/api/types';
import { useOiStore } from '@/lib/store/oi';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Section, SectionGroup } from '@/components/Section';
import { InfoRow } from '@/components/InfoRow';
import { Dot } from '@/components/ui/dot';
import { formatUtc } from '@/lib/format';
import { datumLabel } from '@/lib/mqtt/oiAltitude';
import { cn } from '@/lib/utils';
import { rtmStatusTone } from '@/lib/oi/rtmStatus';
import { oiStatusFill } from './oiStyle';

/** Humanise a duration in minutes for the OI detail's Schedule row.
 *  Broker can ship long-range OVs (e.g. 4-month declaration windows ≈
 *  180 000 min) — raw minutes are unreadable. Renders as
 *  "45 min" / "3 h 20 m" / "5 d 4 h" / "125 d" depending on magnitude. */
function formatDuration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const totalMinutes = Math.round(minutes);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const mins = totalMinutes % 60;
  if (days >= 2) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (days === 1) return `${24 + hours} h`;
  return mins > 0 ? `${hours} h ${mins} m` : `${hours} h`;
}

/** Volume altitude, or "—". Never renders a 0 stand-in for "not published". */
function altText(m: number | null | undefined): string {
  return m != null ? `${m.toFixed(2)} m` : '—';
}

export function OIDetail({ id }: { id: string }) {
  const dss = useExternalOis();
  const dssOi = dss.data?.find((o) => o.oi_id === id);
  if (dssOi) return <ThirdPartyOIDetail oi={dssOi} />;
  return <TenantOIDetail id={id} />;
}

/** Mockup `selectExternalOI` with `source === '3rdparty'`. A DSS search
 *  result carries only operator / type / status / window / volume, so this
 *  is deliberately thin — there is no tenant, USS or conformance to show. */
function ThirdPartyOIDetail({ oi }: { oi: DssOi }) {
  const select = useOiStore((s) => s.select);
  const status = oi.status.charAt(0).toUpperCase() + oi.status.slice(1);
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <button
          type="button"
          onClick={() => select(null)}
          className="flex items-center gap-1 text-(--color-green) text-[0.846em] font-semibold mb-1 hover:opacity-80"
        >
          <ArrowLeft size={13} /> Active OI
        </button>
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-bold">{oi.oi_id}</h2>
          <span className="text-[0.692em] font-bold px-1.5 py-0.5 rounded bg-(--color-purple) text-(--color-on-accent)">
            {status}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[0.615em] font-bold px-1.5 py-0.5 rounded-[3px] bg-(--color-purple) text-(--color-on-accent)">
            {oi.operator}
          </span>
          <span className="text-[0.615em] font-bold px-1.5 py-0.5 rounded-[3px] bg-(--color-purple) text-(--color-on-accent)">
            3rd Party
          </span>
        </div>
        <div className="text-[0.692em] font-mono text-(--color-text3) mt-1.5">
          Source: {oi.operator} (DSS)
        </div>
      </header>
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <span className="inline-block text-[0.615em] font-bold px-2 py-0.5 rounded-[3px] border border-(--color-purple)/25 bg-(--color-purple)/12 text-(--color-purple) mb-2.5">
          External DSS Discovery
        </span>
        <SectionGroup>
          <Section title="Operation Summary">
            <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
              <InfoRow label="Operator" value={oi.operator} />
              <InfoRow label="Operation Type" value={oi.op_type} />
              <InfoRow label="Status" value={status} />
              {oi.state_raw && <InfoRow label="DSS State" value={oi.state_raw} />}
              <InfoRow label="Start" value={formatUtc(oi.start_utc)} mono />
              <InfoRow label="End" value={formatUtc(oi.end_utc)} mono />
            </div>
          </Section>
          <Section title="Source">
            <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
              <InfoRow label="Operator (Unaffiliated)" value={oi.operator} />
              <InfoRow label="Discovered via" value="DSS region search" />
              {oi.uss_base_url && <InfoRow label="USS Base URL" value={oi.uss_base_url} mono />}
            </div>
          </Section>
        </SectionGroup>
        <p className="text-[0.692em] font-mono text-(--color-text3) mt-2 leading-normal">
          External OI data · Read-only · Returned by the DSS for this search region, not
          affiliated with any known tenant or partner
        </p>
      </div>
    </div>
  );
}

function TenantOIDetail({ id }: { id: string }) {
  const { data, isPending, isError } = useOiDetailLive(id);
  const select = useOiStore((s) => s.select);
  const isPartner = data?.origin === 'partner';
  // Only label the datum the publisher actually declared.
  const datum = datumLabel(data?.alt_datum ?? null);
  const altDatumSuffix = datum ? ` (${datum})` : '';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <button
          type="button"
          onClick={() => select(null)}
          className="flex items-center gap-1 text-(--color-green) text-[0.846em] font-semibold mb-1 hover:opacity-80"
        >
          <ArrowLeft size={13} /> Active OI
        </button>
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-bold">{id}</h2>
          {data &&
            (isPartner ? (
              <span
                className={cn(
                  'text-[0.692em] font-bold px-1.5 py-0.5 rounded text-(--color-on-accent)',
                  oiStatusFill(data.status),
                )}
              >
                {data.status}
              </span>
            ) : (
              <Badge variant={rtmStatusTone(data.status)}>{data.status}</Badge>
            ))}
        </div>
        {data && isPartner && (
          <>
            {/* Mockup `selectExternalOI`: aircraft badge + "Partner" in place
                of the conformance badge, both on the tenant accent; the
                updated line becomes "Source: {tenant}". */}
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-[0.615em] font-bold px-1.5 py-0.5 rounded-[3px] bg-(--color-partner) text-(--color-on-accent)">
                {data.aircraft}
              </span>
              <span className="text-[0.615em] font-bold px-1.5 py-0.5 rounded-[3px] bg-(--color-partner) text-(--color-on-accent)">
                Partner
              </span>
            </div>
            <div className="text-[0.692em] font-mono text-(--color-text3) mt-1.5">
              Source: {data.partner_tenant ?? '—'}
            </div>
          </>
        )}
        {data && !isPartner && (
          <>
            <div className="flex items-center gap-1.5 mt-1">
              {data.aircraft && <Badge variant="solid">{data.aircraft}</Badge>}
            </div>
            {/* Mockup `id="oi-detail-updated"` (L3290 + L10570) — last-update
                timestamp in the header, falls back to "—". */}
            <div className="text-[0.692em] font-mono text-(--color-text3) mt-1.5">
              Updated: {data.last_updated ? formatUtc(data.last_updated) : '—'}
            </div>
          </>
        )}
      </header>
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {isPending && (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" /> <Skeleton className="h-32 w-full" />{' '}
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {isError && <div className="text-[0.769em] text-(--color-red)">Failed to load OI.</div>}
        {!isPending && !isError && !data && (
          <div className="text-[0.769em] text-(--color-text3)">This OI is no longer published.</div>
        )}
        {data && isPartner && (
          <div className="pb-2">
            <span className="inline-block text-[0.615em] font-bold px-2 py-0.5 rounded-[3px] border border-(--color-partner)/25 bg-(--color-partner)/12 text-(--color-partner) mb-2.5">
              Cross-Tenant Partnership
            </span>
            <SectionGroup>
              <Section title="Operation Summary">
                <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
                  <InfoRow label="Title" value={data.title || '—'} />
                  <InfoRow label="Aircraft" value={data.aircraft} mono />
                  <InfoRow label="Operation Type" value={data.op_type} />
                  <InfoRow label="Status" value={data.status} />
                </div>
              </Section>
              <Section title="Source">
                <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
                  <InfoRow label="Partner / Tenant" value={data.partner_tenant ?? '—'} />
                  {/* The real wire topic, not the mockup's illustrative
                      `flysafe/v1/partner/{tenant}/ois` — partner OIs land on
                      the aircraft's own topic (see lib/tenancy.ts). */}
                  <InfoRow
                    label="Stream Topic"
                    value={`v1/output/aircraft/${data.aircraft}/oi/declaration`}
                    mono
                  />
                </div>
              </Section>
            </SectionGroup>
            <p className="text-[0.692em] font-mono text-(--color-text3) mt-2 leading-normal">
              External OI data · Read-only · Shared via cross-tenant partnership
            </p>
          </div>
        )}
        {data && !isPartner && (
          <SectionGroup>
            <Section title="Flight Summary">
              <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
                <InfoRow label="Aircraft" value={data.aircraft} mono />
                <InfoRow label="Drone Model" value={data.drone_model} />
                <InfoRow label="Operation" value={data.operation} />
                <InfoRow
                  label="Max AGL"
                  value={data.max_agl_m != null ? `${data.max_agl_m} m` : '—'}
                  mono
                />
              </div>
            </Section>
            <Section title="Schedule">
              <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
              <InfoRow label="Start" value={formatUtc(data.start_utc)} mono />
              <InfoRow label="End" value={formatUtc(data.end_utc)} mono />
              <InfoRow label="Duration" value={formatDuration(data.duration_min)} mono />
              {(data as any).time_added_total_s != null && (
                <InfoRow
                  label="Time Added"
                  value={`${(data as any).time_added_total_s}s${(data as any).time_added_max_s != null ? ` / ${(data as any).time_added_max_s}s max` : ''}`}
                  mono
                />
              )}
            </div>
          </Section>
            <Section title="Flight Volume">
              <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
                {/* The datum is whatever the publisher declared — it is not
                    assumed. Before 2026-08-24 these rows were hardcoded
                    "(WGS84)" while being fed waypoint AGL. */}
                <InfoRow
                  label={`Alt Lower${altDatumSuffix}`}
                  value={altText(data.alt_lower_m)}
                  mono
                />
                <InfoRow
                  label={`Alt Upper${altDatumSuffix}`}
                  value={altText(data.alt_upper_m)}
                  mono
                />
              </div>
            </Section>
            {/* Mockup §"DSS Submission" L10604-L10608 — UTM Provider /
                Submitted / Status / Flight Decl ID / OI Ref ID. */}
            <Section title="DSS Submission">
              <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
                <InfoRow label="UTM Provider" value={data.uss_availability} />
                <InfoRow
                  label="Submitted"
                  value={data.submission_date ? formatUtc(data.submission_date) : '—'}
                  mono
                />
                <InfoRow
                  label="Status"
                  value={data.submission_status ?? data.dss_status}
                  valueClassName="text-(--color-green)"
                />
                <InfoRow label="Flight Decl. ID" value={data.flight_decl_id ?? '—'} mono />
                <InfoRow label="OI Ref ID" value={data.oi_ref_id} mono />
              </div>
            </Section>
            {/* Mockup §"USS / Interoperability" L10611-L10614 — Manager USS,
                USS Base URL, Subscription ID, OVN. */}
            <Section title="USS / Interoperability">
              <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
                <InfoRow label="Manager USS" value={data.manager_uss ?? '—'} />
                <InfoRow label="USS Base URL" value={data.uss_base_url} mono />
                <InfoRow label="Subscription ID" value={data.subscription_id ?? '—'} mono />
                <InfoRow label="OVN" value={data.ovn} mono />
              </div>
            </Section>
            <Section title="RTM Subscribers">
              <ul className="space-y-1">
                {data.subscribers.map((sub) => (
                  <li
                    key={sub.name}
                    className="flex items-center justify-between rounded-md border border-(--color-border) bg-(--color-surface) px-2.5 py-1.5"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Dot
                        variant={
                          sub.status === 'active'
                            ? 'ok'
                            : sub.status === 'subscribed'
                              ? 'info'
                              : 'warn'
                        }
                        size="xs"
                      />
                      <span className="text-[0.769em] font-bold truncate">{sub.name}</span>
                    </div>
                    <span className="text-[0.615em] font-mono text-(--color-text3) uppercase">
                      {sub.role}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          </SectionGroup>
        )}
      </div>
    </div>
  );
}
